import lotsMapping from "../Data/lotsBackground.json";
import { useState, useEffect } from "react";

// Normalisation unique pour matcher les noms de lot (DB, JSON, API) :
// minuscules, sans diacritiques, ligatures oe/ae developpees, trim.
const normalizeLot = (s: string): string =>
  (s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/œ/g, "oe")
    .replace(/æ/g, "ae")
    .trim();

const imagesContext = (require as any).context(
  "../assets/image-lot",
  false,
  /\.webp$/,
);

// Cache en memoire pour les URLs d'images venant de l'API Core
let apiImagesCache: Record<string, string> = {};
let loadPromise: Promise<void> | null = null;

/**
 * Charge (une seule fois) les images depuis l'API Core et met en cache.
 * Retourne la meme promise si deja en cours de chargement.
 */
export const preloadLotImages = (): Promise<void> => {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const baseUrl = (process.env.REACT_APP_API_URL || "").replace(
        /\/api\/?$/,
        "",
      );
      const res = await fetch(`${baseUrl}/api/lots-technique/images`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const lots: { nom: string; image_url: string | null }[] = json.data ?? [];
      for (const lot of lots) {
        if (lot.image_url) {
          apiImagesCache[normalizeLot(lot.nom)] = lot.image_url;
        }
      }
    } catch {
      // fallback silencieux vers images statiques - reset pour pouvoir reessayer
      loadPromise = null;
    }
  })();
  return loadPromise;
};

// Resolution statique uniquement (mapping image-lot bundle), sans passer par
// le cache API. Sert de fallback quand l'URL API d'une image est cassee
// (onError) : on retombe sur la bonne image locale, pas sur default.jpg.
export const getLotImageStatic = (lotName: string): string => {
  const normalized = normalizeLot(lotName);

  const lot = lotsMapping?.find(
    (l) => normalizeLot(l.lot_name ?? "") === normalized,
  );

  const fileName = lot?.nom_fichier;
  if (!fileName) return imagesContext("./default.webp");

  // Les originaux jpg/jpeg ont ete convertis en webp (meme nom de base).
  const webpName = fileName.replace(/\.(jpe?g|png)$/i, ".webp");
  try {
    return imagesContext(`./${webpName}`);
  } catch {
    console.warn(`Image non trouvee pour le lot : ${lotName}`);
    return imagesContext("./default.webp");
  }
};

export const getLotImage = (lotName: string): string => {
  const normalized = normalizeLot(lotName);

  // Priorite 1 : image locale bundlee (webp) si le lot est mappe -> instantane, zero reseau
  const hasLocal = lotsMapping?.some(
    (l) => normalizeLot(l.lot_name ?? "") === normalized && !!l.nom_fichier,
  );
  if (hasLocal) {
    return getLotImageStatic(lotName);
  }

  // Priorite 2 : URL API Core si le lot n'a pas d'image locale
  if (apiImagesCache[normalized]) {
    return apiImagesCache[normalized];
  }

  // Priorite 3 : fallback statique (default.webp)
  return getLotImageStatic(lotName);
};

/**
 * Hook React qui retourne l'image d'un lot en attendant le preload de l'API.
 * Demarre avec l'image statique (fallback), puis met a jour si l'API repond.
 */
export const useLotImage = (lotName: string | undefined): string => {
  const [image, setImage] = useState<string>(() => getLotImage(lotName ?? ""));

  useEffect(() => {
    if (!lotName) return;
    preloadLotImages().then(() => {
      setImage(getLotImage(lotName));
    });
  }, [lotName]);

  return image;
};
